import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { List, LayoutGrid, Filter, Search, CheckSquare, Check, Clock, Plus, MoreVertical, Bot } from 'lucide-react';
import { taskService, type Task } from '../../../services/taskApi.ts';
import AddTaskModal from '../../../components/modals/AddTaskModal.tsx';
import AddMultipleTasksModal from '../../../components/modals/AddMultipleTasksModal.tsx';
import { PageTracker } from '../../../components/PageTracker.tsx';

const AssigneeBadge: React.FC<{ task: Task }> = ({ task }) => {
  if (!task.assignedToUserId) return null;
  const person = task.assigneeProfile;
  const name = person?.name || person?.username || 'User unavailable';
  const initials = person ? name.trim().split(/\s+/).map(part => part[0]).join('').slice(0, 2).toUpperCase() : '?';
  return <div className="inline-flex items-center gap-2 text-xs text-[color:var(--lyra-color-text-muted)]" title={person?.username ? '@' + person.username : name}>
    {person?.profilePicture ? <img src={person.profilePicture} alt="" className="w-6 h-6 rounded-full object-cover" referrerPolicy="no-referrer" /> : <span className="w-6 h-6 rounded-full bg-[color:var(--lyra-color-surface-2)] border border-[color:var(--lyra-color-border)] flex items-center justify-center text-[10px] font-semibold">{initials}</span>}
    <span className="truncate max-w-36">{person?.username ? '@' + person.username : name}</span>
  </div>;
};

const TaskScopeLabel: React.FC<{ task: Task }> = ({ task }) => {
  const teamName = task.spaceName || task.spaces?.[0]?.name;
  const isTeam = !!(task.assignmentTeamId || task.spaceId || task.spaceIds?.length);
  const label = task.projectName ? task.projectName : isTeam ? (teamName || 'Team task') : 'Personal';
  const kind = task.projectName ? 'Project' : isTeam ? 'Team' : 'Personal';
  return <div className="mb-2 flex items-center gap-1.5 min-w-0">
    <span className="inline-flex max-w-full items-center gap-1.5 rounded-md border border-[color:var(--lyra-color-border)] bg-[color:var(--lyra-color-surface-2)] px-2 py-1 text-[11px] font-medium text-[color:var(--lyra-color-text-muted)]" title={kind + ': ' + label}>
      <span className="shrink-0 opacity-70">{kind === 'Team' ? '◉' : kind === 'Project' ? '▦' : '●'}</span>
      <span className="truncate">{label}</span>
    </span>
  </div>;
};

const PersonalKanban: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [decliningId, setDecliningId] = useState<string | null>(null);
  const [declineReason, setDeclineReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [editingProject, setEditingProject] = useState<any>(null);

  const [viewMode, setViewMode] = useState<'kanban' | 'list'>('kanban');
  const [filter, setFilter] = useState({
    status: 'all',
    priority: 'all',
    category: 'all'
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddTask, setShowAddTask] = useState(false);
  const [showMultipleTasksModal, setShowMultipleTasksModal] = useState(false);
  const [projectData, setProjectData] = useState<any>(null);

  // Add state for editing and deleting tasks
  const [editTask, setEditTask] = useState<Task | null>(null);
  const [showEditTaskModal, setShowEditTaskModal] = useState(false);

  // Add state for menu visibility
  const [activeMenuTaskId, setActiveMenuTaskId] = useState<string | null>(null);

  const predefinedCategories = [
    'App Idea',
    'Web App',
    'Social App',
    'EdTech',
    'Business Idea',
    'Product Concept',
    'Feature Request',
    'Research Topic',
    'Creative Project',
    'Other'
  ];

  // Load tasks when component mounts
  useEffect(() => {
    const loadTasks = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const allTasks = await taskService.getTasks();
        console.log('Loaded tasks in personal-kanban:', allTasks.length, 'tasks');
        
        // Transform API response to match our expected Task interface
        const tasksWithValidStatus = allTasks.map(task => ({
          ...task,
          // The TaskService now handles status mapping, so we just use the provided status
          status: task.status || (task.completed ? 'Done' : 'Todo') as 'Todo' | 'InProgress' | 'Done'
        }));
        
        setTasks(tasksWithValidStatus);
      } catch (err) {
        console.error('Error loading tasks:', err);
        setError('Failed to load tasks. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    loadTasks();
  }, []);

  // Handle navigation from projects page
  useEffect(() => {
    if (location.state?.openModal && location.state?.projectData) {
      setProjectData(location.state.projectData);
      setShowMultipleTasksModal(true);
      
      // Clear the state to prevent reopening on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleTaskAdded = (newTask: Task) => {
    // Add the new task to the state
    setTasks(prevTasks => [...prevTasks, newTask]);
  };

  const handleMultipleTasksAdded = (newTasks: Task[]) => {
    // Add multiple new tasks to the state
    setTasks(prevTasks => [...prevTasks, ...newTasks]);
  };

  const pendingAssignments = tasks.filter(t => t.assignmentStatus === 'pending');
  const respond = async (id: string, action: 'accept' | 'decline') => {
    try {
      const updated = await taskService.respondToAssignment(id, action, action === 'decline' ? declineReason : undefined);
      setTasks(prev => prev.map(t => t._id === id ? updated : t));
      setDecliningId(null); setDeclineReason('');
    } catch (e) { setError(e instanceof Error ? e.message : 'Unable to respond'); }
  };

  const columns = [
    { id: 'Todo', title: 'To Do', accent: 'var(--app-text-3)' },
    { id: 'InProgress', title: 'In Progress', accent: 'var(--lyra-color-primary)' },
    { id: 'Done', title: 'Done', accent: 'var(--lyra-color-success)' }
  ];

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'High': return 'bg-red-100 text-red-800 border-red-200';
      case 'Medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-[color:var(--lyra-color-text)] border-[color:var(--lyra-color-border)]';
    }
  };

  const moveTask = async (taskId: string, newStatus: 'Todo' | 'InProgress' | 'Done') => {
    try {
      // Update task status via API
      const updatedTask = await taskService.updateTaskStatus(taskId, newStatus);
      
      // Update local state
      setTasks(tasks.map(task =>
        task._id === taskId ? updatedTask : task
      ));
    } catch (err) {
      console.error('Error updating task status:', err);
      setError('Failed to update task status. Please try again.');
    }
  };

  const toggleTask = async (taskId: string) => {
    try {
      const task = tasks.find(t => t._id === taskId);
      if (!task) return;

      // Cycle through the status: Todo -> InProgress -> Done -> Todo
      const updatedTask = await taskService.cycleTaskStatus(taskId, task.status);
      
      // Update local state with the new task
      setTasks(tasks.map(t => t._id === taskId ? updatedTask : t));
    } catch (err) {
      console.error('Error toggling task status:', err);
      setError('Failed to update task. Please try again.');
    }
  };

  const isOverdue = (dueDate: string) => {
    return new Date(dueDate) < new Date() && dueDate !== '';
  };

  const isDueSoon = (dueDate: string) => {
    const due = new Date(dueDate);
    const today = new Date();
    const diffTime = due.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 3 && diffDays >= 0;
  };

  const filteredTasks = tasks.filter(task => {
    if (task.assignmentStatus === 'pending' || task.assignmentStatus === 'declined') return false;
    const matchesStatus = filter.status === 'all' || 
                         (filter.status === 'done' && task.status === 'Done') ||
                         (filter.status === 'inProgress' && task.status === 'InProgress') ||
                         (filter.status === 'todo' && task.status === 'Todo');
    
    const matchesPriority = filter.priority === 'all' || 
                           task.priority.toLowerCase() === filter.priority;
    
    const matchesCategory = filter.category === 'all' || task.category === filter.category;
    
    const matchesSearch = task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         task.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (task.tagsList && task.tagsList.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())));
    
    return matchesStatus && matchesPriority && matchesCategory && matchesSearch;
  });

  // Handle task editing
  const handleEditTask = (task: Task) => {
    setEditTask(task);
    setShowEditTaskModal(true);
  };

  // Handle task deletion
  const handleDeleteTask = async (taskId: string) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    try {
      await taskService.deleteTask(taskId);
      setTasks(prev => prev.filter(t => t._id !== taskId));
    } catch (err) {
      setError('Failed to delete task. Please try again.');
    }
  };

   // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (activeMenuTaskId && !(event.target as Element).closest('.relative')) {
          setActiveMenuTaskId(null);
        }
      };
  
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [activeMenuTaskId]);

      // ── Design tokens (matches Workflows.jsx exactly) ────────────────────────────
const T = {
  page:        "var(--lyra-color-surface)",
  surface:     "var(--lyra-color-surface)",
  surface2:    "var(--lyra-color-surface-2)",
  surface3:    "var(--app-surface-3)",
  border:      "var(--lyra-color-border)",
  border2:     "var(--app-border-strong)",
  ink:         "var(--lyra-color-text)",
  ink2:        "var(--app-text-2)",
  ink3:        "var(--lyra-color-text-muted)",
  ink4:        "var(--app-text-3)",
  accent:      "var(--lyra-color-primary)",
  accentLight: "var(--app-primary-soft)",
  accentMid:   "var(--app-primary-soft-2)",
  accentDark:  "var(--app-primary-deep)",
  green:       "var(--lyra-color-success)",
  greenBg:     "var(--lyra-color-success-bg)",
  greenBd:     "var(--app-success-border)",
  red:         "var(--lyra-color-danger)",
  redBg:       "var(--lyra-color-danger-bg)",
  redBd:       "var(--app-danger-border)",
  amber:       "var(--lyra-color-warning)",
  amberBg:     "var(--lyra-color-warning-bg)",
  amberBd:     "var(--app-warning-border)",
};

  return (
    <div className="project-mgmt-page" style={{ minHeight: "100vh", background: T.page, fontFamily: "DM Sans, system-ui, sans-serif", padding: "20px 24px 40px" }}>
      <PageTracker pageTitle="Kanban Board" category="project-management" />
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div>
            <div style={{ fontSize: 22, fontWeight: 600, color: T.ink, marginBottom: 4 }}>Kanban Board</div>
            <p style={{ color: T.ink4 }} className='text-sm'>Organize and track your tasks with Kanban board or list view</p>
          </div>
          
          {/* View Toggle and Add Task */}
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/tools/project-management/project-agent')}
              className="bg-[color:var(--lyra-color-primary)] text-white px-4 py-2 rounded-lg hover:bg-[color:var(--app-primary-deep)] transition-colors flex items-center space-x-2"
            >
              <Bot className="w-4 h-4" />
              <span style={{ color: "var(--app-on-accent)" }}>Ask Agent</span>
            </button>
            <button
              onClick={() => setShowAddTask(true)}
              className="bg-[color:var(--lyra-color-primary)] text-white px-4 py-2 rounded-lg hover:bg-[color:var(--app-primary-deep)] transition-colors flex items-center space-x-2"
            >
              <Plus className="w-4 h-4" />
              <span style={{ color: "var(--app-on-accent)" }}>Add Task</span>
            </button>
            
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('kanban')}
                className={`flex items-center px-4 py-2 rounded-md transition-all ${
                  viewMode === 'kanban'
                    ? 'bg-white text-[color:var(--lyra-color-primary)] shadow-sm'
                    : 'text-[color:var(--lyra-color-text-muted)] hover:text-[color:var(--lyra-color-text)]'
                }`}
              >
                <LayoutGrid className="w-4 h-4 mr-2" />
                Kanban
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`flex items-center px-4 py-2 rounded-md transition-all ${
                  viewMode === 'list'
                    ? 'bg-white text-[color:var(--lyra-color-primary)] shadow-sm'
                    : 'text-[color:var(--lyra-color-text-muted)] hover:text-[color:var(--lyra-color-text)]'
                }`}
              >
                <List className="w-4 h-4 mr-2" />
                List
              </button>
            </div>
          </div>
        </div>

        {pendingAssignments.length > 0 && <section style={{ border: '1px solid var(--lyra-color-border)', borderRadius: 12, padding: 16, marginBottom: 16 }} aria-label="Pending assignments">
          <h3 style={{ fontWeight: 600, marginBottom: 12 }}>Pending assignments ({pendingAssignments.length})</h3>
          {pendingAssignments.map(t => <div key={t._id} style={{ padding: 12, border: '1px solid var(--lyra-color-border)', borderRadius: 8, marginBottom: 8 }}>
            <strong>{t.title}</strong><p style={{ fontSize: 12 }}>{t.description}</p>
            {decliningId === t._id ? <div><textarea aria-label="Reason for declining" value={declineReason} onChange={e => setDeclineReason(e.target.value)} maxLength={500} placeholder="Reason for declining (required)" /><button disabled={!declineReason.trim()} onClick={() => respond(t._id, 'decline')}>Confirm decline</button><button onClick={() => setDecliningId(null)}>Cancel</button></div> : <div style={{ display: 'flex', gap: 8, marginTop: 8 }}><button onClick={() => respond(t._id, 'accept')}>Accept</button><button onClick={() => setDecliningId(t._id)}>Decline</button></div>}
          </div>)}
        </section>}
        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow-sm border border-[color:var(--lyra-color-border)] p-4 mb-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <div className="flex-1 min-w-64">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[color:var(--app-text-3)] w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search tasks..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-[color:var(--app-border-strong)] rounded-lg focus:ring-2 focus:ring-[color:var(--lyra-color-primary)] focus:border-transparent"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div className="flex items-center space-x-2">
              <Filter className="w-4 h-4 text-[color:var(--app-text-3)]" />
              <select
                value={filter.status}
                onChange={(e) => setFilter({ ...filter, status: e.target.value })}
                className="border border-[color:var(--app-border-strong)] rounded-lg px-3 py-2 focus:ring-2 focus:ring-[color:var(--lyra-color-primary)] focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="todo">To Do</option>
                <option value="inProgress">In Progress</option>
                <option value="done">Done</option>
              </select>
            </div>

            {/* Priority Filter */}
            <select
              value={filter.priority}
              onChange={(e) => setFilter({ ...filter, priority: e.target.value })}
              className="border border-[color:var(--app-border-strong)] rounded-lg px-3 py-2 focus:ring-2 focus:ring-[color:var(--lyra-color-primary)] focus:border-transparent"
            >
              <option value="all">All Priority</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>

            {/* Category Filter */}
            <select
              value={filter.category}
              onChange={(e) => setFilter({ ...filter, category: e.target.value })}
              className="border border-[color:var(--app-border-strong)] rounded-lg px-3 py-2 focus:ring-2 focus:ring-[color:var(--lyra-color-primary)] focus:border-transparent"
            >
              <option value="all">All Categories</option>
              {predefinedCategories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Content Area */}
        {loading ? (
          <div className="bg-white rounded-lg shadow-sm border border-[color:var(--lyra-color-border)] p-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[color:var(--lyra-color-primary)] mx-auto mb-4"></div>
              <p className="text-[color:var(--lyra-color-text-muted)]">Loading tasks...</p>
            </div>
          </div>
        ) : error ? (
          <div className="bg-white rounded-lg shadow-sm border border-[color:var(--lyra-color-border)] p-8">
            <div className="text-center text-red-600">
              <p className="font-medium">Error loading tasks</p>
              <p className="text-sm mt-1">{error}</p>
              <button 
                onClick={() => window.location.reload()} 
                className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Retry
              </button>
            </div>
          </div>
        ) : viewMode === 'kanban' ? (
          // Kanban View
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {columns.map(column => {
              const columnTasks = filteredTasks.filter(task => task.status === column.id);

              return (
                <div
                  key={column.id}
                  className="bg-[color:var(--lyra-color-surface-2)] border border-[color:var(--lyra-color-border)] border-t-2 rounded-lg p-3"
                  style={{ borderTopColor: column.accent }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.add('ring-2', 'ring-blue-400', 'ring-opacity-50');
                  }}
                  onDragLeave={(e) => {
                    e.currentTarget.classList.remove('ring-2', 'ring-blue-400', 'ring-opacity-50');
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove('ring-2', 'ring-blue-400', 'ring-opacity-50');
                    const draggedTaskId = e.dataTransfer.getData('text/plain');
                    if (draggedTaskId) {
                      moveTask(draggedTaskId, column.id as 'Todo' | 'InProgress' | 'Done');
                    }
                  }}
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-[color:var(--lyra-color-text)]">{column.title}</h3>
                    <span className="bg-white text-[color:var(--lyra-color-text-muted)] text-sm px-2 py-1 rounded-full">
                      {columnTasks.length}
                    </span>
                  </div>
                  
                  <div className="space-y-2">
                    {columnTasks.length === 0 ? (
                      <div className="text-center py-6 text-[color:var(--app-text-3)] border-2 border-dashed border-[color:var(--lyra-color-border)] rounded-lg">
                        <p className="text-sm">Drop tasks here</p>
                      </div>
                    ) : (
                      columnTasks.map(task => (
                        <div
                          key={task._id}
                          className="bg-white rounded-lg p-3 shadow-sm border border-[color:var(--lyra-color-border)] hover:shadow-md transition-shadow cursor-move"
                          draggable
                          onDragStart={(e) => {
                            e.dataTransfer.setData('text/plain', task._id);
                            e.currentTarget.classList.add('opacity-50');
                          }}
                          onDragEnd={(e) => {
                            e.currentTarget.classList.remove('opacity-50');
                          }}
                        >
                          <TaskScopeLabel task={task} />
                          <div className="flex items-start justify-between mb-1.5">
                            <h4 className="font-medium text-[color:var(--lyra-color-text)] flex-1">{task.title}</h4>
                            <div className="relative">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  console.log('Dropdown button clicked for task:', task._id);
                                  // setActiveDropdown(activeDropdown === task._id ? null : task._id);
                                  setActiveMenuTaskId(activeMenuTaskId === task._id ? null : task._id);
                                }}
                                className="p-1.5 rounded-lg text-[color:var(--app-text-3)] hover:text-[color:var(--lyra-color-text-muted)] hover:bg-[color:var(--lyra-color-surface)] transition-colors">
                                <MoreVertical className="w-4 h-4" />
                              </button>
                            
                            {activeMenuTaskId === task._id && (
                              <div className="absolute right-0 w-32 bg-white border rounded shadow-lg z-10">
                                <button className="w-full px-3 py-2 text-left text-sm hover:bg-[color:var(--lyra-color-surface-2)]" onClick={() => { handleEditTask(task); setActiveMenuTaskId(null); }}>Edit</button>
                                <button className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50" onClick={() => { handleDeleteTask(task._id); setActiveMenuTaskId(null); }}>Delete</button>
                              </div>
                            )}
                          </div>
                          </div>

                          <div className="mb-2"><AssigneeBadge task={task} /></div>
                          {/* Space task badges — agent that owns this task + who created it */}
                          {task.spaceId && (
                            <div className="flex items-center flex-wrap gap-1.5 mb-1.5">
                              {(task.agentName || (typeof task.agentId === 'object' && task.agentId?.name)) && (
                                <span className="inline-flex items-center gap-1 bg-violet-50 text-violet-700 text-[10.5px] px-2 py-0.5 rounded-full font-semibold">
                                  <Bot className="w-2.5 h-2.5" /> {task.agentName || (task.agentId as any)?.name}
                                </span>
                              )}
                              {(task.createdByName || (typeof task.createdBy === 'object' && task.createdBy?.name)) && (
                                <span className="text-[10.5px] text-[color:var(--app-text-3)]">by {task.createdByName || (task.createdBy as any)?.name}</span>
                              )}
                            </div>
                          )}

                          <p className="text-[color:var(--lyra-color-text-muted)] text-sm mb-2">{task.description}</p>

                          <div className="flex items-center justify-between text-xs text-[color:var(--app-text-3)]">
                            <div className="flex items-center space-x-2">
                              <Clock className="w-3 h-3" />
                              <span>{task.estimatedTimeInHours}h</span>
                            </div>
                            {task.dueDate && (
                              <span className={`px-2 py-1 rounded ${
                                isOverdue(task.dueDate) ? 'bg-red-100 text-red-700' :
                                isDueSoon(task.dueDate) ? 'bg-yellow-100 text-yellow-700' :
                                'bg-gray-100 text-[color:var(--lyra-color-text-muted)]'
                              }`}>
                                {new Date(task.dueDate).toLocaleDateString()}
                              </span>
                            )}
                          </div>

                          {task.tagsList && task.tagsList.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1.5">
                              {task.tagsList.map((tag, index) => (
                                <span
                                  key={index}
                                  className="bg-gray-100 text-[color:var(--lyra-color-text-muted)] text-xs px-2 py-1 rounded"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}

                          {/* Status Action Buttons */}
                          <div className="flex items-center justify-between mt-2 pt-2 border-t border-[color:var(--lyra-color-border)]">
                            <div className="flex gap-1">
                              {columns.map(col => (
                                <button
                                  key={col.id}
                                  onClick={() => moveTask(task._id, col.id as 'Todo' | 'InProgress' | 'Done')}
                                  className={`px-2 py-1 text-xs rounded transition-colors ${
                                    task.status === col.id
                                      ? col.id === 'Todo' ? 'bg-[color:var(--app-surface-3)] text-[color:var(--lyra-color-text)]' :
                                        col.id === 'InProgress' ? 'bg-[color:var(--app-primary-soft-2)] text-[color:var(--app-primary-deep)]' :
                                        'bg-green-200 text-green-800'
                                      : 'bg-[color:var(--lyra-color-surface)] text-[color:var(--lyra-color-text-muted)] hover:bg-[color:var(--lyra-color-surface-2)]'
                                  }`}
                                  disabled={task.status === col.id}
                                >
                                  {col.id === 'InProgress' ? 'Progress' : col.id}
                                </button>
                              ))}
                            </div>
                            <span className={`text-xs px-2 py-1 rounded-full border ${getPriorityColor(task.priority)}`}>
                              {task.priority}
                            </span>
                          </div>
                        </div>
                      ))
                    )}
                </div>
              </div>
              );
            })}
          </div>
        ) : (
          // List View
          <div className="bg-white rounded-lg shadow-sm border border-[color:var(--lyra-color-border)]">
            <div className="p-6">
              <div className="space-y-2">
                {filteredTasks.map(task => (
                  <div
                    key={task._id}
                    className={`flex items-center justify-between p-4 rounded-lg border transition-colors ${
                      task.status === 'Done' 
                        ? 'bg-gray-50 border-[color:var(--lyra-color-border)]' 
                        : 'bg-white border-[color:var(--lyra-color-border)] hover:bg-[color:var(--lyra-color-surface)]'
                    }`}
                  >
                    <div className="flex items-center space-x-4 flex-1">
                      <button
                        onClick={() => toggleTask(task._id)}
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                          task.status === 'Done'
                            ? 'bg-green-500 border-green-500 text-white'
                            : 'border-[color:var(--app-border-strong)] hover:border-green-400'
                        }`}
                      >
                        {task.status === 'Done' && <Check className="w-3 h-3" />}
                      </button>
                      
                      <div className="flex-1">
                        <h4 className={`font-medium ${task.status === 'Done' ? 'text-[color:var(--app-text-3)] line-through' : 'text-[color:var(--lyra-color-text)]'}`}>
                          {task.title}
                        </h4>
                        <p className={`text-sm ${task.status === 'Done' ? 'text-[color:var(--app-text-3)]' : 'text-[color:var(--lyra-color-text-muted)]'}`}>
                          {task.description}
                        </p>
                        {task.tagsList && task.tagsList.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {task.tagsList.map((tag, index) => (
                              <span
                                key={index}
                                className="bg-gray-100 text-[color:var(--lyra-color-text-muted)] text-xs px-2 py-1 rounded"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-4">
                      <AssigneeBadge task={task} />
                      <span className={`text-xs px-2 py-1 rounded-full border ${getPriorityColor(task.priority)}`}>
                        {task.priority}
                      </span>
                      
                      <div className="flex items-center space-x-1 text-xs text-[color:var(--app-text-3)]">
                        <Clock className="w-3 h-3" />
                        <span>{task.estimatedTimeInHours}h</span>
                      </div>
                      
                      {task.dueDate && (
                        <span className={`text-xs px-2 py-1 rounded ${
                          isOverdue(task.dueDate) ? 'bg-red-100 text-red-700' :
                          isDueSoon(task.dueDate) ? 'bg-yellow-100 text-yellow-700' :
                          'bg-gray-100 text-[color:var(--lyra-color-text-muted)]'
                        }`}>
                          {new Date(task.dueDate).toLocaleDateString()}
                        </span>
                      )}
                      
                      <span className={`text-xs px-2 py-1 rounded ${
                        task.status === 'Done' ? 'bg-green-100 text-green-700' :
                        task.status === 'InProgress' ? 'bg-[color:var(--app-primary-soft)] text-[color:var(--lyra-color-primary)]' :
                        'bg-gray-100 text-[color:var(--lyra-color-text-muted)]'
                      }`}>
                        {task.status === 'Todo' ? 'To Do' : 
                         task.status === 'InProgress' ? 'In Progress' : 'Done'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              
              {filteredTasks.length === 0 && (
                <div className="text-center py-12">
                  <CheckSquare className="w-16 h-16 text-[color:var(--app-border-strong)] mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-[color:var(--lyra-color-text)] mb-2">No tasks found</h3>
                  <p className="text-[color:var(--app-text-3)]">Try adjusting your filters or search term</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Stats Section */}
        <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg shadow-sm border border-[color:var(--lyra-color-border)] p-4">
            <h3 className="text-sm font-semibold text-[color:var(--lyra-color-text)] mb-3">Quick Stats</h3>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-[color:var(--lyra-color-text-muted)]">Total Tasks:</span>
                <span className="font-semibold text-lg">{tasks.length}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[color:var(--lyra-color-text-muted)]">Completed:</span>
                <span className="font-semibold text-lg text-green-600">
                  {tasks.filter(t => t.status === 'Done').length}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[color:var(--lyra-color-text-muted)]">In Progress:</span>
                <span className="font-semibold text-lg text-[color:var(--lyra-color-primary)]">
                  {tasks.filter(t => t.status === 'InProgress').length}
                </span>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm border border-[color:var(--lyra-color-border)] p-4">
            <h3 className="text-sm font-semibold text-[color:var(--lyra-color-text)] mb-3">Priority Breakdown</h3>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-red-600">High Priority:</span>
                <span className="font-semibold text-lg">{tasks.filter(t => t.priority === 'High').length}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-yellow-600">Medium Priority:</span>
                <span className="font-semibold text-lg">{tasks.filter(t => t.priority === 'Medium').length}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-green-600">Low Priority:</span>
                <span className="font-semibold text-lg">{tasks.filter(t => t.priority === 'Low').length}</span>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm border border-[color:var(--lyra-color-border)] p-4">
            <h3 className="text-sm font-semibold text-[color:var(--lyra-color-text)] mb-3">Productivity</h3>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-[color:var(--lyra-color-text-muted)]">Completion Rate:</span>
                <span className="font-semibold text-lg">
                  {tasks.length > 0 ? Math.round((tasks.filter(t => t.status === 'Done').length / tasks.length) * 100) : 0}%
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[color:var(--lyra-color-text-muted)]">Due Soon:</span>
                <span className="font-semibold text-lg text-orange-600">
                  {tasks.filter(t => t.dueDate && isDueSoon(t.dueDate) && t.status !== 'Done').length}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[color:var(--lyra-color-text-muted)]">Overdue:</span>
                <span className="font-semibold text-lg text-red-600">
                  {tasks.filter(t => t.dueDate && isOverdue(t.dueDate) && t.status !== 'Done').length}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Add Task Modal */}
        <AddTaskModal
          isOpen={showAddTask}
          onClose={() => setShowAddTask(false)}
          onTaskAdded={handleTaskAdded}
          mode="task-management"
        />

        {/* Add Multiple Tasks Modal */}
        <AddMultipleTasksModal
          isOpen={showMultipleTasksModal}
          onClose={() => {
            setShowMultipleTasksModal(false);
            setProjectData(null);
          }}
          onTasksAdded={handleMultipleTasksAdded}
          projectData={projectData}
        />

        {/* Edit Task Modal */}
        {showEditTaskModal && editTask && (
          <AddTaskModal
            isOpen={showEditTaskModal}
            onClose={() => { setShowEditTaskModal(false); setEditTask(null); }}
            onTaskAdded={(updatedTask) => {
              setTasks(prev => prev.map(t => t._id === updatedTask._id ? updatedTask : t));
              setShowEditTaskModal(false);
              setEditTask(null);
            }}
            mode="task-management"
            defaultDay={editTask.day || 1}
            currentWeekDates={[]}
            task={editTask} // Pass the task data for edit mode
          />
        )}
      </div>
    </div>
  );
};

export default PersonalKanban;
